import {withClientState} from 'apollo-link-state';
import {InMemoryCache, defaultDataIdFromObject} from 'apollo-cache-inmemory';
import {GetRepairsCart} from '../../../services/SmsService/query.graphql';
import {GetCustomer, GetSelectedPaymentOption} from '../../../services/Customer/query.graphql';

export const cache = new InMemoryCache( {
    dataIdFromObject: obj => {
        switch ( obj.__typename ) {
            case 'Address':
                // addresses coming from SMS will always have an id of "0" which breaks apollo caching
                return !obj.id || obj.id ==='0'
                    ? `${obj.__typename}:${obj.address1.replace( /[^a-z0-9]/gi, '' )}`
                    : `${obj.__typename}:${obj.id}`;

            case 'CartLineItem':
            case 'OrderLineItem':
                return `${obj.__typename}:${obj.id}`;

            case 'Product':
                return `${obj.__typename}:${obj.sku}`;

            case 'Taxonomy':
            case 'Distributor':
                return `${obj.__typename}:${obj.sysId}`;

            case 'Account':
            case 'Wallet':
                return `${obj.__typename}:${obj.accountNumber}`;

            case 'Customer':
                return `${obj.__typename}:${obj.userGuid}`;

            case 'UpsLabel':
                return `${obj.__typename}:${obj.trackingNumber}`;

            case 'Rma':
                return `${obj.__typename}:${obj.requestId}`;

            case 'RmaLineItem':
                return `${obj.__typename}:${obj.requestUnitId}`;


            case 'SelectedPayment':
                return 'Customer:selectedPayment';

            case 'SmsProduct':
            case 'RepairCartLineItem':
                return `${obj.__typename}:${obj.serialId}`;

            default:
                return defaultDataIdFromObject( obj );
        }
    }
} );

const defaults = {
    smsCart: [],
    selectedPayment: {
        paymentId: null,
        billingAddressId: null,
        PONumber: '',
        __typename: 'SelectedPayment'
    }
};

const typeDefs = `
    type RepairCartLineItem: {
        serialId: String!
        equipmentIssue: String!
        injury: Boolean!    
    }
    type RepairsCart: [SmsProduct]
    
    type PaymentOption {
        paymentId: String
        billingAddressId: String
        PONumber: String
    }
    
    Mutation: {
        addRepairItem(input: SmsProductInput!): RepairsCart
        removeRepairItem(input: SmsProductInput!): RepairsCart
        updateRepairItems(input: [SmsProductInput]!): RepairsCart
        setSelectedPaymentOption(input: [PaymentOptionInput]): PaymentOption
    }
    Query {
        smsCart: [SmsProduct]
        selectedPayment: PaymentOption
    }
`;

const resolvers = {
// note signature of resolver --> fieldName: (obj, variables, context, info) => result;
// resource --> https://www.apollographql.com/docs/react/essentials/local-state.html
    Mutation: {
        // resource -> https://www.apollographql.com/docs/react/essentials/local-state/#managing-the-cache
        removeRepairItem: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetRepairsCart} );
            const smsCart = store.smsCart.filter( ( {serialId} ) => serialId !== input.serialId );
            cache.writeData( {data: {smsCart}} );

            return input;
        },

        addRepairItem: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetRepairsCart} );
            const itemExists = store.smsCart.find( ( {serialId} ) => serialId === input.serialId );

            // note only allow single add
            if ( !itemExists ) {
                cache.writeData( {data: {smsCart: store.smsCart.concat( input )}} );
            }

            return input;
        },

        updateRepairsCart: ( _, {input}, {cache, getCacheKey} ) => {
            const smsCart = input != null ? input : [];
            cache.writeData( {data: {smsCart}} );
            return smsCart;
        },

        setSelectedPaymentOption: ( _, {input}, {cache, getCacheKey} ) => {
            const store = cache.readQuery( {query: GetSelectedPaymentOption} );
            const newSelectedPayment = {
                ...store.selectedPayment,
                ...input
            };
            cache.writeData( {data: {selectedPayment: newSelectedPayment}} );

            return newSelectedPayment;
        }
    }
};

const stateLink = withClientState( {resolvers, cache, defaults, typeDefs} );

export default stateLink;

